import React, { useEffect, useState, useRef } from 'react'
import { Form, Input, Select, Button, Typography, AutoComplete } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'

const { Text } = Typography

const states = {
    "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
    "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA",
    "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL", "Indiana": "IN", "Iowa": "IA",
    "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
    "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS", "Missouri": "MO",
    "Montana": "MT", "Nebraska": "NE", "Nevada": "NV", "New Hampshire": "NH", "New Jersey": "NJ",
    "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH",
    "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
    "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT",
    "Virginia": "VA", "Washington": "WA", "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY"
}

const formatTenDigitPhoneNumber = (phone) => {
    if (!phone) return 'N/A'
    const digits = phone.replace(/\D/g, '')
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    return phone
}

export const PharmacySearchForm = ({
    pharmacySearchResults,
    pharmacySpecialtyTypes,
    searchPharmacies,
    selectPharmacy,
    handleCancelSearch,
}) => {
    const [form] = Form.useForm()
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const searchResultsRef = useRef()

    const handleSearch = async () => {
        const formVals = form.getFieldsValue()
        
        const filteredVals = Object.fromEntries(
            Object.entries(formVals).filter(([key, val]) => val)
        )
          
        searchPharmacies(filteredVals)
    }

    useEffect(() => {
        setDropdownOpen(pharmacySearchResults.length > 0)
        if (searchResultsRef.current) {
            searchResultsRef.current.focus()
        }
        return () => form.resetFields()
    }, [pharmacySearchResults, form])

    const PharmacyOption = ({ pharmacy }) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text style={{ fontWeight: 'bolder' }}>{pharmacy.name}</Text>
                <Text>{formatTenDigitPhoneNumber(pharmacy.phoneOrFax)}</Text>
                <Text>{pharmacy.address1}</Text>
                <Text>{`${pharmacy.city}, ${pharmacy.state} ${pharmacy.zipCode}`}</Text>
                {pharmacy.primaryPhone && <Text>{pharmacy.primaryPhone}</Text>}
                {pharmacy.specialties && <Text>{pharmacy.specialties.join(', ')}</Text>}
            </div>
        )
    }

    const handleClearForm = () => {
        form.resetFields()
        handleCancelSearch()
    }

    return (
        <Form
            id="pharmacy-search-form"
            form={form}
            layout="vertical"
            initialValues={{ newDefaultPharmacy: false }}
        >
            <Button
                id="pharmacy-search-reset-btn"
                icon={<ReloadOutlined />}
                size='small'
                style={{
                    display: 'block',
                    marginLeft: 'auto',
                    position: 'relative',
                    top: '20px',
                    zIndex: 2
                }}
                onClick={handleClearForm}
            >
                Reset Field
            </Button>
            
            <Form.Item
                name='name'
                label='Pharmacy Name'
            >
                <AutoComplete
                    id="pharmacy-search-name-autocomplete"
                    style={{ width: '100%' }}
                    onSelect={selectPharmacy}
                    open={dropdownOpen}
                    onBlur={() => setDropdownOpen(false)}
                    onFocus={() => setDropdownOpen(true)}
                    ref={searchResultsRef}
                >
                    {pharmacySearchResults?.map(pharmacy => (
                        <Select.Option key={pharmacy.pharmacyId} value={pharmacy.name}>
                            <PharmacyOption pharmacy={pharmacy} />
                        </Select.Option>
                    ))}
                </AutoComplete>
            </Form.Item>
            
            <Form.Item
                name='address'
                label='Address'
            >
                <Input id="pharmacy-search-address-input" />
            </Form.Item>
            
            <Form.Item>
                <Form.Item
                    name='city'
                    label="City"
                    style={{ display: 'inline-block', width: 'calc(50% - 12px)' }}
                >
                    <Input id="pharmacy-search-city-input" />
                </Form.Item>
                <span style={{ display: 'inline-block', width: '24px', lineHeight: '32px', textAlign: 'center' }}></span>
                <Form.Item
                    name='state'
                    label='State'
                    style={{ display: 'inline-block', width: 'calc(50% - 12px)' }}
                >
                    <Select
                        id="pharmacy-search-state-select"
                        allowClear
                        showSearch
                        options={Object.keys(states).map(state => ({ value: state, label: state }))}
                    />
                </Form.Item>
            </Form.Item>
            
            <Form.Item>
                <Form.Item
                    name='zip'
                    label="Zip Code"
                    style={{ display: 'inline-block', width: 'calc(50% - 12px)' }}
                >
                    <Input id="pharmacy-search-zip-input" />
                </Form.Item>
                <span style={{ display: 'inline-block', width: '24px', lineHeight: '32px', textAlign: 'center' }}></span>
                <Form.Item
                    name='phone'
                    label='Phone'
                    style={{ display: 'inline-block', width: 'calc(50% - 12px)' }}
                >
                    <Input id="pharmacy-search-phone-input" />
                </Form.Item>
            </Form.Item>
            
            <Form.Item
                name='specialty'
                label='Specialty'
            >
                <Select
                    id="pharmacy-search-specialty-select"
                    allowClear
                    options={Object.entries(pharmacySpecialtyTypes || {}).map(([key, value]) => ({ value, label: key }))}
                />
            </Form.Item>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <Button id="pharmacy-search-cancel-btn" onClick={handleCancelSearch}>
                    Cancel
                </Button>
                <Button id="pharmacy-search-submit-btn" type="primary" onClick={handleSearch}>
                    Search
                </Button>
            </div>
        </Form>
    )
}
